"use client";

import React, {useCallback, useRef, useState} from "react";
import toast from "react-hot-toast";
import {AiFillCloseCircle, AiOutlinePaperClip} from "react-icons/ai";

type Props = {
  value: any[];
  onChangeFile: (next: any[]) => void;
  max?: number;
  className?: string;
  onError?: (msg: string) => void;
};

export function UploadFormField({
  value,
  onChangeFile,
  max = 3,
  className = "grid-cols-2",
  onError
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_MIME = new Set(["image/jpeg", "image/png", "application/pdf"]);
  const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "pdf"]);

  function validateFile(file: File) {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const okMime = ALLOWED_MIME.has(file.type);
    const okExt = ALLOWED_EXT.has(ext);
    return okMime || okExt;
  }
  const error = (msg: string) => {
    if (onError) onError(msg);
    else alert(msg);
  };

  const addFiles = useCallback(
    (list: FileList | null) => {
      if (!list) return;
      const files = Array.from(list);
      const valid = files.filter(validateFile);
      const rejected = files.filter(f => !validateFile(f));
      if (rejected.length) {
        toast.error(`Invalid files`);
        return;
      }

      const remaining = Math.max(0, max - value.length);
      if (list.length > remaining) {
        toast.error(`Maximum ${max} files can be uploaded`);
        return;
      }

      const now = Date.now();
      const incoming: any[] = Array.from(list)
        .slice(0, remaining)
        .map((f, i) => ({
          id: `new-${now}-${i}`,
          name: f.name,
          sizeKB: Math.max(1, Math.round(f.size / 1024)),
          source: "new" as const,
          file: f
        }));

      if (incoming.length) onChangeFile([...value, ...incoming].slice(0, max));
    },
    [value, onChangeFile, max]
  );

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (id: string) => {
    onChangeFile(value.filter(x => x.id !== id));
  };

  return (
    <div className={`grid items-stretch gap-4 ${className}`}>
      <div className="flex flex-col">
        <h4 className="mb-2 text-sm font-semibold text-gray-900">
          Document Upload
        </h4>

        <label
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          className={[
            "flex h-40 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed bg-white",
            isDragging ? "border-green-400" : "border-gray-300"
          ].join(" ")}
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            multiple
            onChange={e => {
              addFiles(e.target.files);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-green-100"
            onClick={() => inputRef.current?.click()}
            aria-label="Upload files"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
            >
              <path strokeWidth="1.5" d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
            </svg>
          </button>
          <p className="mt-2 text-sm">
            <span
              className="font-semibold text-green-600"
              onClick={() => inputRef.current?.click()}
            >
              Click to Upload
            </span>{" "}
            <span className="text-gray-500">or drag and drop</span>
          </p>
        </label>
        <p className="mt-2 text-xs text-[#3B82F6]">
          *Maximum {max} files can be uploaded.
        </p>
      </div>
      <div
        className={`flex flex-col ${className?.includes("grid-cols-1") ? "mt-1" : "mt-6"}`}
      >
        {value.map(f => (
          <div
            key={f.id}
            className="mb-1 flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-2"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center">
                <AiOutlinePaperClip className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm font-medium text-gray-900">
                  {f.name}
                </p>
                <p className="text-xs text-gray-500">{f.sizeKB}kb</p>
                {/*f.source === "existing" && f.url && (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 underline"
                  >
                    View / Download
                  </a>
                )*/}
              </div>
            </div>

            <button
              type="button"
              onClick={() => removeItem(f.id)}
              className="rounded-full p-1 text-rose-500 hover:bg-rose-50"
              aria-label={`Remove ${f.name}`}
              title="Remove"
            >
              <AiFillCloseCircle className="h-6 w-6" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
